import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core'
import { CommonModule } from '@angular/common'
import { ButtonModule } from 'primeng/button'

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { Router, RouterModule } from '@angular/router'
import { FmAvatarComponent } from '@app/shared/components/fm-avatar/fm-avatar.component'
import { HomeWelcomeMessageComponent } from '@app/right-panel/components/home-welcome-message/home-welcome-message.component'
import { Subject, takeUntil } from 'rxjs'
import { UserService } from '@app/core/_services/user.service'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import { GENDER_MASK_WOMAN, User } from '@app/core/_models'
import { faUserGroup, faUsersMedical, faVideo } from '@fortawesome/pro-light-svg-icons'
import { faHandWave } from '@fortawesome/pro-regular-svg-icons'
import { faGear } from '@fortawesome/pro-light-svg-icons'
import { faGift } from '@fortawesome/pro-light-svg-icons'
import { AuthService } from '@app/core/_services'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { faUserTieHair, faUserTieHairLong } from '@fortawesome/pro-light-svg-icons'
import { UpcomingSessionsService } from '@app/left-panel/services/upcoming-sessions.service'
import { FeatureCheckService } from '@app/core/_services/feature-check.service'
import { TagModule } from 'primeng/tag'
import { MessageModule } from 'primeng/message'
import { AmbassadorSessionsBadgeComponent } from '@app/shared/components/ambassador-sessions-badge/ambassador-sessions-badge.component'
import { HostBadgeComponent } from '@app/shared/components/host-badge/host-badge.component'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { FmIconComponent } from '@app/shared/components/fm-icon/fm-icon.component'
@Component({
    selector: 'fm-mobile-menu',
    imports: [
        CommonModule,
        FontAwesomeModule,
        RouterModule,
        FmAvatarComponent,
        HomeWelcomeMessageComponent,
        ButtonModule,
        TagModule,
        MessageModule,
        AmbassadorSessionsBadgeComponent,
        HostBadgeComponent,
        FmIconComponent,
    ],
    templateUrl: './mobile-menu.component.html',
    styleUrl: './mobile-menu.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileMenuComponent {
    @Output() menuItemClicked = new EventEmitter<void>()
    public currentUser: User = new User()
    public planNameString: string = ''
    public showUpgradeButton: boolean
    public trialDaysRemaining: number | null = null
    public hasYearlyReview: boolean = false

    public faHandWave = faHandWave
    public faUserGroup = faUserGroup
    public faGear = faGear
    public faGift = faGift
    public faVideo = faVideo
    public faUsersMedical = faUsersMedical
    public faAdminIcon = faUserTieHair
    public isGroupAdmin: boolean
    public isFmAdmin: boolean

    private ngUnsubscribe: Subject<any> = new Subject<any>()

    constructor(
        private analyticsService: AnalyticsService,
        private authService: AuthService,
        private userService: UserService,
        private dialogService: DialogService,
        private router: Router,
        public upcomingSessionsService: UpcomingSessionsService,
        private rewardsReferralService: RewardsReferralService,
        public welcomeChecklistService: WelcomeChecklistService,
        public featureCheckService: FeatureCheckService,
    ) {}

    ngOnInit() {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.currentUser = { ...user }
            this.hasYearlyReview = !!this.currentUser?.yearlyReview
            if (this.currentUser.genderSelection & GENDER_MASK_WOMAN) {
                this.faAdminIcon = faUserTieHairLong
            } else {
                this.faAdminIcon = faUserTieHair
            }
            this.setPlanNameSInfo()
            this.isFmAdmin = this.currentUser.fmAd
            this.isGroupAdmin = this.currentUser.group.some((g) => g.groupAdmin === true)
        })
    }

    private setPlanNameSInfo(): void {
        if (this.currentUser.planInfo.productAccess == 'Focusmate Free') {
            this.showUpgradeButton = true
            this.planNameString = 'Free Plan · 3 sessions per week'
        } else {
            this.showUpgradeButton = false
            this.planNameString = this.currentUser.planInfo.productAccess
        }
        this.trialDaysRemaining = this.rewardsReferralService.getTrialDaysRemaining(
            this.currentUser,
        )
    }

    public onClickLogOut(): void {
        this.analyticsService.logClickedEvent('Sign Out', 'mobileMenu')
        this.authService.signOut('mobileMenu')
    }

    onClickMenuItem(): void {
        this.menuItemClicked.emit()
    }

    onClickYearInFocus(): void {
        this.analyticsService.logClickedEvent('Year in Focus', 'Mobile Menu')
        this.dialogService.openYearInReviewModal()
    }

    onClickGroupSessions(): void {
        if (!this.isGroupMeetingsEnabled()) {
            return
        }
        this.analyticsService.logGroupMeetingClick('openGroupMeetings', 'mobileMenu')
        this.router.navigate(['/group-meetings'])
        this.onClickMenuItem()
    }

    isGroupMeetingsEnabled(): boolean {
        return this.featureCheckService.isGroupMeetingsEnabled(this.currentUser)
    }

    isTaskManagerEnabled(): boolean {
        return this.featureCheckService.isTaskManagerEnabled(this.currentUser)
    }

    openHelpFeedbackModal(): void {
        this.analyticsService.logClickedEvent('Help and Feedback', 'mobileMenu')
        this.dialogService.openHelpFeedbackModal()
        this.onClickMenuItem()
    }
}
